/**
 * 🏆 ChainLegendBoard — weekly Extra Spin streak leaderboard.
 * Sits inside the Hall of Champions (owner directive Jul 2026).
 *
 * Endpoint: GET /api/spin/chain-legend?limit=5
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChainLegendBoard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/spin/chain-legend?limit=5`);
        if (!cancelled) setData(r.data);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const top = data.top || [];

  return (
    <section
      className="mb-6 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-[#100022] via-black/40 to-[#1a0522] p-5"
      data-testid="chain-legend-board"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Flame className="text-orange-400" size={22} />
          <div
            className="text-xl font-black tracking-wide text-purple-200"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            CHAIN LEGENDS · WEEK OF {data.week_start}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          Longest Extra-Spin chain this week
        </div>
      </div>

      {top.length === 0 ? (
        <div className="text-center text-neutral-500 text-sm py-6" data-testid="chain-legend-empty">
          No chains logged this week — be the first to hit a 4-in-a-row Extra Spin combo.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="chain-legend-list">
          {top.map((row, idx) => (
            <li
              key={row.user_id}
              className={
                "flex items-center gap-3 rounded-lg border p-2.5 " +
                (idx === 0
                  ? "border-amber-500/50 bg-amber-500/10 shadow-[0_0_20px_rgba(245,158,11,0.25)]"
                  : "border-neutral-800 bg-black/40")
              }
              data-testid={`chain-legend-row-${idx + 1}`}
            >
              <div
                className={
                  "w-8 h-8 rounded-full flex items-center justify-center font-black text-sm " +
                  (idx === 0
                    ? "bg-amber-500 text-black"
                    : idx === 1
                    ? "bg-neutral-300 text-black"
                    : idx === 2
                    ? "bg-orange-700 text-white"
                    : "bg-neutral-800 text-neutral-400")
                }
              >
                {idx + 1}
              </div>
              {row.avatar_url ? (
                <img src={row.avatar_url} alt={row.name} className="w-8 h-8 rounded-full object-cover border border-neutral-700" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-purple-900/40 border border-purple-500/40 flex items-center justify-center text-sm font-bold text-purple-200">
                  {(row.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-neutral-100 flex items-center gap-1.5 truncate">
                  {row.name || "Anonymous"}
                  {row.chain_master && (
                    <span
                      title="Chain Master badge"
                      className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/50 text-orange-200 font-semibold"
                    >
                      🔥 CM
                    </span>
                  )}
                </div>
                {row.handle && (
                  <div className="text-[10px] text-neutral-500 font-mono">@{row.handle}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-orange-300 leading-none">
                  {row.chain_length}
                </div>
                <div className="text-[8px] uppercase tracking-widest text-neutral-500">
                  chain
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Viewer's rank */}
      {data.my_streak > 0 && (
        <div
          className="mt-3 rounded-lg border border-purple-500/30 bg-black/40 p-2.5 flex items-center justify-between"
          data-testid="chain-legend-my-rank"
        >
          <div className="text-xs text-neutral-300">
            <span className="text-neutral-500">Your best chain this week:</span>{" "}
            <b className="text-orange-300">{data.my_streak}</b>
          </div>
          {data.my_rank && (
            <div className="text-[10px] uppercase tracking-widest text-purple-300">
              Rank #{data.my_rank}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
